<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <div class="folder-grid-container" v-if="favoriteFolders.length > 0">
    <button
      v-for="folder in favoriteFolders"
      :key="folder.id"
      class="folder-card"
      @click="navigateTo(folder)"
      :aria-label="folder.name"
    >
      <div class="folder-card-row">
        <i class="ri-folder-line folder-icon" aria-hidden="true"></i>
        <div class="folder-info">
          <div class="folder-name" v-tooltip.bottom="folder.name">
            {{ folder.name }}
          </div>
        </div>
      </div>
    </button>
  </div>
  <div v-else>{{ $t("emptyFavoriteFolder") }}</div>
</template>

<script>
import { useConfigStore } from "@/stores/config";
import { useFavoritesStore } from "@/stores/favorites";
import { mapState } from "pinia";
import Tooltip from "primevue/tooltip";

export default {
  name: "FavoriteFolderList",
  directives: {
    tooltip: Tooltip,
  },
  props: {
    siteId: {
      type: String,
      default: "",
    },
    urlResolver: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      favorites: [],
    };
  },
  computed: {
    ...mapState(useConfigStore, ["ESPACES_HOST"]),
    favoriteFolders() {
      return this.favorites
        .map((fav) => fav.target.folder)
        .filter((folder) => folder !== undefined && folder !== null)
        .filter((folder) => this.matchesSite(folder))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
  },
  mounted() {
    const favoritesStore = useFavoritesStore();
    favoritesStore
      .loadFavorites()
      .then(() => {
        this.favorites = favoritesStore.folderFavorites;
      })
      .catch((err) => {
        console.error("Error loading favorites:", err);
      });
  },
  methods: {
    matchesSite(node) {
      if (!this.siteId) {
        return true;
      }
      const siteElement = node.path?.elements?.find(
        (el) => el.nodeType === "st:site",
      );
      return siteElement?.name === this.siteId;
    },
    navigateTo(folder) {
      if (this.urlResolver) {
        this.urlResolver(folder);
      } else {
        window.open(
          `${this.ESPACES_HOST}/espaces/mes-espaces/dossier/${folder.id}`,
          "_blank",
        );
      }
    },
  },
};
</script>

<style scoped>
.folder-grid-container {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
}

.folder-card {
  background-color: var(--p-content-background);
  padding: 1rem;
  border-radius: 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--p-content-border-color);
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  font-size: inherit;
  color: inherit;
  transition:
    border-color 0.2s,
    box-shadow 0.2s;
}

.folder-card:hover {
  border-color: var(--p-primary-color);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.folder-card:focus-visible {
  outline: 2px solid var(--p-primary-color);
  outline-offset: 2px;
}

.folder-card-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.folder-icon {
  font-size: 1.5rem;
  color: var(--p-primary-color);
  flex-shrink: 0;
}

.folder-info {
  min-width: 0;
  overflow: hidden;
}

.folder-info > div {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.folder-name {
  font-weight: 500;
  font-size: 0.875rem;
}
</style>
